// ReportsList.jsx — v161.22.24
//
// Lists the tester's bug reports (drafts + submitted) with status badges.
// Empty state nudges them to create their first report against an app
// they're testing. The list links to either the editor (drafts) or the
// read-only detail page (submitted+).

import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { bugReports } from '../lib/api.js';

const STATE_BADGE = {
  draft:                { label: 'Draft',             color: '#94a3b8', bg: 'rgba(148,163,184,0.15)' },
  submitted:            { label: 'Submitted',         color: '#a29bfe', bg: 'rgba(108,92,231,0.15)' },
  fixing:               { label: 'Fixing',            color: '#f59e0b', bg: 'rgba(245,158,11,0.15)' },
  reopened:             { label: 'Reopened (retry)',  color: '#f59e0b', bg: 'rgba(245,158,11,0.20)' },
  fixed:                { label: 'Fixed',             color: '#10b981', bg: 'rgba(16,185,129,0.15)' },
  needs_human_review:   { label: 'Needs review',      color: '#ef4444', bg: 'rgba(239,68,68,0.15)' },
  likely_duplicate:     { label: 'Likely duplicate',  color: '#f59e0b', bg: 'rgba(245,158,11,0.10)' },
  archived:             { label: 'Archived',          color: '#64748b', bg: 'rgba(100,116,139,0.10)' }
};

export default function ReportsList() {
  const [reports, setReports] = useState(null);
  const [error, setError] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    bugReports.list()
      .then(r => setReports(r.reports || []))
      .catch(e => setError(e.message));
  }, []);

  if (error) return <div className="error">Error: {error}</div>;
  if (reports === null) return <div className="loading">Loading…</div>;

  // v161.22.27: hide archived by default — they clutter the active list.
  const visible = showArchived ? reports : reports.filter(r => r.state !== 'archived');
  const archivedCount = reports.filter(r => r.state === 'archived').length;

  return (
    <div className="page">
      <header className="page-header">
        <h1>Bug Reports</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <Link to="/reports/upload-pdf" className="btn btn-secondary">📄 Upload PDF</Link>
          <Link to="/reports/new" className="btn btn-primary">+ New Report</Link>
        </div>
      </header>

      {visible.length === 0 ? (
        <div className="empty-state">
          <p>{showArchived ? 'No reports.' : 'No active bug reports.'}</p>
          <p className="muted">
            Tap <strong>+ New Report</strong> to start a testing session.
            You can add multiple bugs to one report and submit them all at once
            — they'll be batched into a single auto-fix run.
          </p>
          {archivedCount > 0 && !showArchived && (
            <p>
              <button className="btn btn-text" onClick={() => setShowArchived(true)}>
                Show {archivedCount} archived
              </button>
            </p>
          )}
        </div>
      ) : (
        <>
          <ul className="report-list">
            {visible.map(r => {
              const badge = STATE_BADGE[r.state] || STATE_BADGE.draft;
              const target = r.state === 'draft' ? `/reports/${r.id}/edit` : `/reports/${r.id}`;
              return (
                <li key={r.id} className="report-list-item">
                  <Link to={target} className="report-link">
                    <div className="row-main">
                      <span className="title">
                        {r.title || `Untitled session report`}
                      </span>
                      <span
                        className="badge"
                        style={{ color: badge.color, background: badge.bg }}
                      >
                        {badge.label}
                      </span>
                    </div>
                    <div className="row-meta muted">
                      <span>{r.app_slug}</span>
                      {r.app_version && <span> · v{r.app_version}</span>}
                      {r.device_model && <span> · {r.device_model}</span>}
                      <span> · {r.entry_count} {r.entry_count === 1 ? 'bug' : 'bugs'}</span>
                      <span> · {timeAgo(r.created_at)}</span>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
          {archivedCount > 0 && (
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button
                className="btn btn-text"
                onClick={() => setShowArchived(s => !s)}
              >
                {showArchived ? 'Hide archived' : `Show ${archivedCount} archived`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
